import { useAuth } from '@/lib/auth/auth';
import {
  buildFeedPath,
  FEED_LIMIT,
  nearbyFeedKeys,
  type FeedMode,
} from '@/features/events/feed/feedQuery';
import type { FeedPreset } from '@/features/events/feed/eventsFeedShared';
import type { FeedDistanceFilter, FeedTimeWindow } from '@/features/events/feed/feedPreferences';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import type { EntryType, FeedResponse } from '@/lib/api/apiTypes';
import type { AnalyticsRequestInit } from '@/lib/api/api';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

type NearbyFeedQueryParams = {
  attributes?: string[];
  entryType?: EntryType | null;
  hasSession: boolean;
  mode: FeedMode;
  originLatitude: number;
  originLongitude: number;
  originSource: string;
  tagGroup?: FeedPreset;
  maxPriceCents?: number | null;
  radiusMeters: FeedDistanceFilter;
  timeWindow: FeedTimeWindow;
  viewerKey: string;
};

type UseNearbyFeedParams = {
  mode: FeedMode;
  radiusMeters: FeedDistanceFilter;
  timeWindow: FeedTimeWindow;
  entryType?: EntryType | null;
  attributes?: string[];
  tagGroup?: FeedPreset;
  maxPriceCents?: number | null;
  enabled?: boolean;
};

type RequestJSON = <T>(path: string, init?: AnalyticsRequestInit) => Promise<T>;

export const NEARBY_FEED_STALE_TIME_MS = 30_000;

export function getNearbyFeedQueryKey(params: NearbyFeedQueryParams) {
  return nearbyFeedKeys.list({
    latitude: getNearbyFeedLatitude(params),
    longitude: getNearbyFeedLongitude(params),
    originLatitude: params.originLatitude,
    originLongitude: params.originLongitude,
    radiusMeters: params.radiusMeters,
    timeWindow: params.timeWindow,
    mode: params.mode,
    entryType: params.entryType,
    tagGroup: params.tagGroup,
    attributes: params.attributes,
    maxPriceCents: params.maxPriceCents,
    viewerKey: params.viewerKey,
  });
}

export function buildNearbyFeedPagePath(params: NearbyFeedQueryParams, offset: number) {
  return buildFeedPath({
    latitude: getNearbyFeedLatitude(params),
    longitude: getNearbyFeedLongitude(params),
    originLatitude: params.originLatitude,
    originLongitude: params.originLongitude,
    radiusMeters: params.radiusMeters,
    timeWindow: params.timeWindow,
    limit: FEED_LIMIT,
    offset,
    mode: params.mode,
    entryType: params.entryType,
    tagGroup: params.tagGroup,
    attributes: params.attributes,
    maxPriceCents: params.maxPriceCents,
  });
}

export function getNearbyFeedNextPageParam(
  lastPage: FeedResponse,
  _pages: FeedResponse[],
  lastOffset: number
) {
  const pageSize = lastPage.items?.length ?? lastPage.events.length;
  return pageSize === FEED_LIMIT ? lastOffset + pageSize : undefined;
}

export function getNearbyFeedQueryOptions(params: NearbyFeedQueryParams, request: RequestJSON) {
  return {
    queryKey: getNearbyFeedQueryKey(params),
    initialPageParam: 0,
    staleTime: NEARBY_FEED_STALE_TIME_MS,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      request<FeedResponse>(buildNearbyFeedPagePath(params, pageParam), {
        analyticsAction: 'load_feed',
      }),
    getNextPageParam: getNearbyFeedNextPageParam,
  };
}

function getNearbyFeedLatitude(params: NearbyFeedQueryParams) {
  // Authenticated approximate origins let the server resolve the user's market instead of leaking stale coords.
  return params.originSource === 'gps' || !params.hasSession ? params.originLatitude : null;
}

function getNearbyFeedLongitude(params: NearbyFeedQueryParams) {
  return params.originSource === 'gps' || !params.hasSession ? params.originLongitude : null;
}

export function useNearbyFeed({
  mode,
  radiusMeters,
  timeWindow,
  entryType = null,
  attributes = [],
  tagGroup = 'all',
  maxPriceCents = null,
  enabled = true,
}: UseNearbyFeedParams) {
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const request = useAuth((state) => state.request);
  const origin = useUserOriginCoords();
  const viewerKey = session?.me.user.id ?? 'anon';
  const queryParams = {
    attributes,
    entryType,
    hasSession: Boolean(session),
    mode,
    originLatitude: origin.latitude,
    originLongitude: origin.longitude,
    originSource: origin.source,
    tagGroup,
    maxPriceCents,
    radiusMeters,
    timeWindow,
    viewerKey,
  };

  return useInfiniteQuery({
    ...getNearbyFeedQueryOptions(queryParams, request),
    enabled: enabled && !isHydrating,
    placeholderData: keepPreviousData,
  });
}
